import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useContext, useMemo, useState, type ReactNode } from "react";
import { RotateCcw } from "lucide-react";
import { useGetBookkeepingAccountsQuery } from "../../query/bookkeepingAccountsQuery";
import { useGetCoursesQuery } from "../../query/coursesQuery";
import { useGetModifierGroupsQuery } from "../../query/modifierGroupsQuery";
import { useGetModifiersQuery } from "../../query/modifiersQuery";
import { useGetPrepStationsQuery } from "../../query/prepStationsQuery";
import {
  useGetProductGroupQuery,
  useGetProductGroupsQuery,
} from "../../query/productGroupsQuery";
import { useGetStoreQuery } from "../../query/storesQuery";
import { useGetUnitsQuery } from "../../query/unitsQuery";
import { useGetStockLocationsQuery } from "../../query/stockLocationsQuery";
import { useGetStockPoliciesQuery } from "../../query/stockPoliciesQuery";
import { Button } from "../../components/Button";
import { Form } from "../../components/Form";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { Input } from "../../components/Input";
import { MoneyInput } from "../../components/MoneyInput";
import { SelectInput } from "../../components/SelectInput";
import { Toggle } from "../../components/Toggle";
import { useGetActiveFeaturesQuery } from "../../query/billingItemsQuery";
import { useCatalogScope } from "../../hooks/useCatalogScope";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type {
  ModifierId,
  ProductGroupId,
  ProductVariantId,
} from "@KassaroAB/shared/types/ids";
import { CatalogOrigin } from "@KassaroAB/shared/types/enums/CatalogOrigin";
import {
  ProductOverlayFieldScheme,
  ProductVariantOverlayFieldScheme,
  type ProductOverlayField,
  type ProductVariantOverlayField,
  type RestUpsertProductOverlay,
} from "@KassaroAB/shared/types/productOverlay";
import colors from "@styling/colors";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { ProductType } from "@KassaroAB/shared/types/enums/ProductType";
import { BarcodeType } from "@KassaroAB/shared/types/enums/BarcodeType";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { LabelDrawableBarcodeSymbology } from "@KassaroAB/shared/types/enums/LabelDrawable";
import type {
  RestCreateProduct,
  RestProduct,
  RestUpdateProduct,
} from "@KassaroAB/shared/types/product";
import type { RestProductGroup } from "@KassaroAB/shared/types/productGroup";
import type { RestStore } from "@KassaroAB/shared/types/store";
import { ProductImportSection } from "./productImport/ProductImportSection";
import { ProductVariantsTable } from "./products/ProductVariantsTable";
import { ProductsTableContext } from "./products/productsTableContext";
import {
  isOverlayFieldLocked,
  overrideMarker,
  overrideTooltip,
  restrictOverlayChanges,
} from "./products/productTableColumns";
import { applyChainValues } from "@KassaroAB/shared/catalog/productOverlay";
import {
  OverlayCell,
  OverlayCellBody,
  OverrideMarker,
  OverrideResetButton,
  ReadOnlyFieldset,
} from "./products/MoneyTableCell";
import { VariantOptionsModal } from "./products/VariantOptionsModal";
import {
  useProductVariantStock,
  type ProductStockChanges,
} from "./products/useProductVariantStock";
import {
  isProductFieldDirty,
  isVariantFieldDirty,
  omitUnchangedVariantStock,
  toOverlayUpdate,
} from "../../utils/productEditDiff";
import {
  getProductGroupLockedValue,
  isProductGroupFieldLocked,
} from "../../utils/productGroupLock";

const TextArea = styled(Input).attrs({ as: "textarea" })`
  min-height: 96px;
  resize: vertical;
  line-height: 1.45;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.x3}px;
  align-items: start;
`;

const GridFull = styled.div`
  grid-column: 1 / -1;
`;

const GridCell = styled.div`
  min-width: 0;
`;

const ToggleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.regular}px;
  cursor: pointer;
`;

const OverrideNote = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.x05}px;
  margin-top: ${spacing.x05}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};

  ${OverrideResetButton} {
    display: inline-flex;
  }
`;

const FormFooter = styled.div`
  padding-top: ${spacing.x2}px;
`;

interface ProductFormProps {
  product?: RestProduct;
  onCreate: (data: RestCreateProduct, stock: ProductStockChanges) => void;
  onUpdate: (data: RestUpdateProduct, stock: ProductStockChanges) => void;
  onUpdateOverlay?: (
    overlay: RestUpsertProductOverlay,
    stock: ProductStockChanges,
  ) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isLoading?: boolean;
}

type ExtraRow = NonNullable<NonNullable<RestStore["extraProductFields"]>[number]>;

const buildExtraDefaults = (
  rows: ExtraRow[] | null | undefined,
  existing: Record<string, string> | null | undefined,
): Record<string, string> => {
  const out: Record<string, string> = {};
  (rows ?? []).forEach((row) => {
    out[row.key] = existing?.[row.key] ?? "";
  });
  return out;
};

const moneyMinorToMajor = (amount: number | undefined) =>
  amount != null ? amount / 100 : 0;

const VAT_OPTIONS = [25, 12, 6, 0] as const;

export const ProductForm = ({
  product,
  onCreate,
  onUpdate,
  onUpdateOverlay,
  onCancel,
  onDelete,
  isLoading,
}: ProductFormProps) => {
  const { t } = useTranslation();
  const tableMeta = useContext(ProductsTableContext)?.tableMeta.productMeta;
  const overlayMeta = tableMeta?.overlay ?? null;
  const [openedSaveTarget] = useState(overlayMeta?.saveTarget);
  const markerMeta =
    tableMeta && overlayMeta && openedSaveTarget
      ? {
          ...tableMeta,
          overlay: { ...overlayMeta, saveTarget: openedSaveTarget },
        }
      : undefined;
  const isChainProduct =
    !!overlayMeta && product?.origin === CatalogOrigin.CHAIN;
  const overlayItem = isChainProduct
    ? overlayMeta.overlays.get(product.id)
    : undefined;
  const overlayStoreMode =
    isChainProduct && openedSaveTarget === "store" && !!onUpdateOverlay;
  const formProduct = useMemo(
    () =>
      product && overlayItem && openedSaveTarget === "chain"
        ? applyChainValues(product, overlayItem.chainValues)
        : product,
    [product, overlayItem, openedSaveTarget],
  );
  const [stagedResets, setStagedResets] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const isLocked = (field: string | null) =>
    isOverlayFieldLocked(markerMeta?.overlay ?? null, product?.origin, field);
  const { scope, storeId } = useCatalogScope();
  const isChain = scope?.type === "chain";
  const { data: store } = useGetStoreQuery(storeId ?? undefined);
  const { data: productGroups } = useGetProductGroupsQuery(scope);
  const { data: units } = useGetUnitsQuery(scope);
  const { data: courses } = useGetCoursesQuery(scope);
  const { data: prepStations } = useGetPrepStationsQuery(scope);
  const { data: modifiersList } = useGetModifiersQuery(scope);
  const { data: modifierGroupsList } = useGetModifierGroupsQuery(scope);
  const { data: bookkeepingAccounts } = useGetBookkeepingAccountsQuery(scope);
  const { data: activeFeatures } = useGetActiveFeaturesQuery(storeId ?? null);
  const storeCurrency = store?.currency ?? Currency.SEK;
  const extraRows = store?.extraProductFields;
  const hasFeature = (feature: Features) =>
    isChain || (activeFeatures?.includes(feature) ?? false);
  const showGroups = hasFeature(Features.GROUPS);
  const showPrep = hasFeature(Features.PREP_TICKETS);
  const showInfoBox = hasFeature(Features.PRODUCT_INFO_BOX);
  const barcodeActive = hasFeature(Features.BARCODE_SCANNING);
  const simpleStock =
    !isChain && (activeFeatures?.includes(Features.SIMPLE_STOCK) ?? false);
  const showRetail = barcodeActive || simpleStock;
  const showExtra =
    !isChain &&
    hasFeature(Features.EXTRA_PRODUCT_FIELDS) &&
    (extraRows?.length ?? 0) > 0;
  const showPurchasePrice = hasFeature(Features.PURCHASE_PRICE);
  const showVariants =
    hasFeature(Features.PRODUCT_VARIANTS) || hasFeature(Features.RESTAURANT_VARIANTS);
  const showModifiers = hasFeature(Features.MODIFIERS);
  const showTakeaway = hasFeature(Features.TAKEOUT_PRICE);
  const showSerialTracking = hasFeature(Features.INVENTORY_COSTING);
  const stockByLocation =
    simpleStock &&
    activeFeatures?.includes(Features.STOCK_LOCATIONS) === true &&
    !!store?.stockLocationId;
  const showStockPolicies =
    simpleStock && activeFeatures?.includes(Features.STOCK_POLICIES) === true;
  const { data: stockLocations = [] } = useGetStockLocationsQuery(
    stockByLocation ? (store?.chainId ?? null) : null,
  );
  const { data: stockPolicies = [] } = useGetStockPoliciesQuery(
    showStockPolicies ? (store?.chainId ?? null) : null,
  );
  const stock = useProductVariantStock({
    storeId: storeId ?? null,
    productId: product?.id,
    defaultStockLocationId: store?.stockLocationId ?? null,
    stockLocations,
    enabled: stockByLocation,
  });
  const [isManagingVariants, setIsManagingVariants] = useState(false);

  const defaultValues = useMemo(() => {
    const extra = buildExtraDefaults(extraRows, formProduct?.extraFields ?? null);
    return {
      name: formProduct?.name ?? "",
      groupId: formProduct?.groupId ?? "",
      unitPriceAmount: moneyMinorToMajor(formProduct?.unitPrice?.amount),
      VAT: formProduct?.VAT ?? 25,
      unitId: formProduct?.unitId ?? "",
      openPrice: formProduct?.openPrice ?? false,
      serialTracking: formProduct?.serialTracking ?? false,
      courseId: formProduct?.courseId ?? "",
      prepStationId: formProduct?.prepStationId ?? "",
      information: formProduct?.information ?? "",
      barcodeCode: formProduct?.barcode?.code ?? "",
      barcodeType: formProduct?.barcode?.type ?? BarcodeType.EAN_13,
      barcodeFormatId: formProduct?.barcode?.format ?? "",
      stockCount:
        formProduct?.stockCount != null && formProduct.stockCount !== undefined
          ? String(formProduct.stockCount)
          : "",
      articleNumber: formProduct?.articleNumber ?? "",
      manufacturerArticleNumber: formProduct?.manufacturerArticleNumber ?? "",
      manufacturerBarcode: formProduct?.manufacturerBarcode ?? "",
      purchasePriceAmount: moneyMinorToMajor(formProduct?.purchasePrice?.amount),
      extraFields: extra,
      variants: formProduct?.variants ?? [],
      modifierGroups: formProduct?.modifierGroups ?? [],
      modifiers: formProduct?.modifiers ?? [],
      alwaysAppliedModifiers: formProduct?.alwaysAppliedModifiers ?? [],
      bookkeepingType: (formProduct?.type === ProductType.GIFT_CARD
        ? ProductType.PRODUCT
        : formProduct?.type) ?? ProductType.PRODUCT,
      bookkeepingAccountId: formProduct?.bookkeepingAccountId ?? "",
      takeawayVAT:
        formProduct?.takeawayVAT === null || formProduct?.takeawayVAT === undefined
          ? ""
          : String(formProduct.takeawayVAT),
      takeawayBookkeepingAccountId: formProduct?.takeawayBookkeepingAccountId ?? "",
      takeawayPriceAmount: moneyMinorToMajor(
        formProduct?.takeawayPrice?.amount,
      ),
    };
  }, [formProduct, extraRows]);

  const form = useForm({
    defaultValues,
    onSubmit: ({ value }) => {
      const unitMinor = Math.round(Number(value.unitPriceAmount) * 100);
      const purchaseMinor = Math.round(Number(value.purchasePriceAmount) * 100);
      const takeawayMinor = Math.round(Number(value.takeawayPriceAmount) * 100);
      const stockParsed = value.stockCount.trim();
      const stockCount =
        stockParsed === "" ? null : Math.trunc(Number(stockParsed));

      const extraFieldKeys = Object.keys(value.extraFields ?? {}).filter((k) =>
        extraRows?.some((r) => r.key === k),
      );
      const extraFieldsRaw: Record<string, string> = {};
      extraFieldKeys.forEach((k) => {
        const v = value.extraFields?.[k];
        if (typeof v === "string" && v.trim() !== "") {
          extraFieldsRaw[k] = v.trim();
        }
      });
      const extraFields =
        Object.keys(extraFieldsRaw).length > 0 ? extraFieldsRaw : undefined;

      const barcodeTrim = value.barcodeCode.trim();
      const barcode =
        barcodeTrim.length > 0
          ? {
              code: barcodeTrim,
              type: value.barcodeType,
              format: value.barcodeFormatId.trim()
                ? value.barcodeFormatId.trim()
                : null,
            }
          : undefined;

      const unitPrice = {
        amount: unitMinor,
        currency: storeCurrency,
      };

      const purchasePrice =
        purchaseMinor > 0
          ? { amount: purchaseMinor, currency: storeCurrency }
          : null;

      const takeawayPrice =
        takeawayMinor > 0
          ? { amount: takeawayMinor, currency: storeCurrency }
          : null;

      const takeawayVATParsed = value.takeawayVAT.trim();
      const takeawayVAT =
        takeawayVATParsed === ""
          ? null
          : Number(takeawayVATParsed);

      const groupId = value.groupId || null;
      const unitId = value.unitId || null;
      const courseId = value.courseId || null;
      const prepStationId = value.prepStationId || null;
      const bookkeepingAccountId = value.bookkeepingAccountId || null;
      const takeawayBookkeepingAccountId =
        value.takeawayBookkeepingAccountId || null;

      const variantIds = new Set(
        (value.variants ?? []).map((variant) => variant.id),
      );
      const isKept = (change: { variantId: string | null }) =>
        change.variantId === null
          ? variantIds.size === 0
          : variantIds.has(change.variantId);
      const allStockChanges = stock.changes();
      const stockChanges: ProductStockChanges = {
        balances: allStockChanges.balances.filter(isKept),
        policies: allStockChanges.policies.filter(isKept),
      };

      const apiType =
        value.bookkeepingType === ProductType.GIFT_CARD
          ? ProductType.PRODUCT
          : value.bookkeepingType;

      if (product && overlayStoreMode && onUpdateOverlay) {
        const changes: RestUpdateProduct = {
          name: value.name,
          unitPrice,
          takeawayPrice: takeawayMinor > 0 ? takeawayPrice : null,
          prepStationId,
          bookkeepingAccountId,
          variants: value.variants,
        };
        const chainProduct = overlayItem
          ? applyChainValues(product, overlayItem.chainValues)
          : product;
        const resetFields = ProductOverlayFieldScheme.options.filter(
          (field) =>
            stagedResets.has(field) &&
            !isProductFieldDirty(chainProduct, changes, field),
        );
        const resetVariantFields: Record<
          ProductVariantId,
          ProductVariantOverlayField[]
        > = Object.fromEntries(
          (value.variants ?? []).flatMap((variant) => {
            const fields = ProductVariantOverlayFieldScheme.options.filter(
              (field) =>
                stagedResets.has(`${variant.id}:${field}`) &&
                !isVariantFieldDirty(chainProduct, changes, variant.id, field),
            );
            return fields.length > 0 ? [[variant.id, fields]] : [];
          }),
        );
        const { variants, ...overlay } = toOverlayUpdate(
          product,
          restrictOverlayChanges(markerMeta?.overlay ?? null, product, changes),
        );
        resetFields.forEach((field) => {
          delete overlay[field];
        });
        const variantOverlays = Object.fromEntries(
          Object.entries(variants ?? {}).flatMap(([variantId, fields]) => {
            const resets: readonly string[] =
              resetVariantFields[variantId] ?? [];
            const kept = Object.fromEntries(
              Object.entries(fields).filter(([field]) => !resets.includes(field)),
            );
            return Object.keys(kept).length > 0 ? [[variantId, kept]] : [];
          }),
        );
        onUpdateOverlay(
          {
            ...overlay,
            ...(resetFields.length > 0 && { resetFields }),
            ...(Object.keys(variantOverlays).length > 0 && {
              variants: variantOverlays,
            }),
            ...(Object.keys(resetVariantFields).length > 0 && {
              resetVariantFields,
            }),
          },
          stockChanges,
        );
        return;
      }

      if (formProduct) {
        const stockChanged =
          value.stockCount.trim() !== defaultValues.stockCount.trim();
        const update: RestUpdateProduct = {
          name: value.name,
          type: apiType,
          unitPrice,
          VAT: value.VAT,
          openPrice: value.openPrice,
          ...(value.serialTracking !== defaultValues.serialTracking
            ? { serialTracking: value.serialTracking }
            : {}),
          prepStationId,
          courseId,
          bookkeepingAccountId,
          groupId,
          unitId,
          articleNumber: value.articleNumber || undefined,
          information: value.information || undefined,
          barcode,
          ...(stockChanged ? { stockCount } : {}),
          manufacturerArticleNumber: value.manufacturerArticleNumber || undefined,
          manufacturerBarcode: value.manufacturerBarcode || undefined,
          purchasePrice: purchaseMinor > 0 ? purchasePrice : null,
          extraFields,
          variants: value.variants?.length
            ? omitUnchangedVariantStock(formProduct.variants, value.variants)
            : undefined,
          modifierGroups: value.modifierGroups?.length
            ? value.modifierGroups
            : undefined,
          modifiers: value.modifiers?.length ? value.modifiers : undefined,
          alwaysAppliedModifiers: value.alwaysAppliedModifiers?.length
            ? value.alwaysAppliedModifiers
            : undefined,
          takeawayVAT,
          takeawayBookkeepingAccountId,
          takeawayPrice: takeawayMinor > 0 ? takeawayPrice : null,
          description: formProduct.description,
          image: formProduct.image,
          tags: formProduct.tags,
        };
        onUpdate(update, stockChanges);
        return;
      }

      const create: RestCreateProduct = {
        name: value.name,
        type: apiType,
        unitPrice,
        VAT: value.VAT,
        openPrice: value.openPrice,
        ...(value.serialTracking ? { serialTracking: true } : {}),
        requireReference: false,
        prepStationId,
        courseId,
        bookkeepingAccountId,
        groupId,
        unitId,
        articleNumber: value.articleNumber || undefined,
        information: value.information || undefined,
        barcode,
        stockCount,
        manufacturerArticleNumber: value.manufacturerArticleNumber || undefined,
        manufacturerBarcode: value.manufacturerBarcode || undefined,
        purchasePrice: purchaseMinor > 0 ? purchasePrice : null,
        extraFields: extraFields ?? null,
        variants: value.variants?.length ? value.variants : undefined,
        modifierGroups: value.modifierGroups?.length
          ? value.modifierGroups
          : undefined,
        modifiers: value.modifiers?.length ? value.modifiers : undefined,
        alwaysAppliedModifiers: value.alwaysAppliedModifiers?.length
          ? value.alwaysAppliedModifiers
          : undefined,
        takeawayVAT,
        takeawayBookkeepingAccountId,
        takeawayPrice: takeawayMinor > 0 ? takeawayPrice : null,
      };
      onCreate(create, stockChanges);
    },
  });

  const stageReset = (
    field: ProductOverlayField,
    variantId?: ProductVariantId,
  ) => {
    if (!overlayItem) {
      return;
    }
    setStagedResets((previous) =>
      new Set(previous).add(variantId ? `${variantId}:${field}` : field),
    );
    const chain = overlayItem.chainValues;
    if (variantId) {
      const chainVariant = chain.variants?.find(
        (variant) => variant.id === variantId,
      );
      form.setFieldValue(
        "variants",
        form
          .getFieldValue("variants")
          .map((variant) =>
            variant.id === variantId
              ? {
                  ...variant,
                  [field]:
                    chainVariant?.[field as ProductVariantOverlayField] ?? null,
                }
              : variant,
          ),
      );
      return;
    }
    switch (field) {
      case "name":
        form.setFieldValue("name", chain.name);
        break;
      case "unitPrice":
        form.setFieldValue(
          "unitPriceAmount",
          moneyMinorToMajor(chain.unitPrice.amount),
        );
        break;
      case "takeawayPrice":
        form.setFieldValue(
          "takeawayPriceAmount",
          moneyMinorToMajor(chain.takeawayPrice?.amount),
        );
        break;
      case "prepStationId":
        form.setFieldValue("prepStationId", chain.prepStationId ?? "");
        break;
      case "bookkeepingAccountId":
        form.setFieldValue(
          "bookkeepingAccountId",
          chain.bookkeepingAccountId ?? "",
        );
        break;
    }
  };

  const overrideNote = (field: ProductOverlayField) => {
    const tooltip =
      markerMeta && product && !stagedResets.has(field)
        ? overrideTooltip(markerMeta, product, field)
        : null;
    if (!tooltip) {
      return null;
    }
    return (
      <OverrideNote>
        <OverrideMarker />
        {tooltip}
        {overlayStoreMode && (
          <OverrideResetButton
            type="button"
            title={t("productsView.overlayReset")}
            aria-label={t("productsView.overlayReset")}
            onClick={() => stageReset(field)}
          >
            <RotateCcw size={12} />
          </OverrideResetButton>
        )}
      </OverrideNote>
    );
  };

  const chainField = (field: ProductOverlayField | null, children: ReactNode) => (
    <>
      <ReadOnlyFieldset disabled={isLocked(field)}>{children}</ReadOnlyFieldset>
      {field && overrideNote(field)}
    </>
  );

  const variantsChain = isChainProduct
    ? {
        isLocked,
        renderCell: (
          variantId: ProductVariantId,
          columnId: string,
          content: ReactNode,
        ) => {
          const field = ProductVariantOverlayFieldScheme.options.find(
            (item) => item === columnId,
          );
          const marker =
            field && markerMeta && product && !stagedResets.has(`${variantId}:${field}`)
              ? overrideMarker(markerMeta, product, field, variantId, () =>
                  stageReset(field, variantId),
                )
              : null;
          return marker ? (
            <OverlayCell>
              <OverlayCellBody>{content}</OverlayCellBody>
              {marker}
            </OverlayCell>
          ) : (
            content
          );
        },
      }
    : undefined;

  const groupId = useStore(form.store, (s) => s.values.groupId);
  const variantsLen = useStore(
    form.store,
    (s) => s.values.variants?.length ?? 0,
  );

  const { data: selectedGroup } = useGetProductGroupQuery(
    scope,
    (groupId || undefined) as ProductGroupId | undefined,
  );

  const locked = (name: keyof RestProductGroup) =>
    isProductGroupFieldLocked(name, selectedGroup);
  const lockedVal = (name: keyof RestProductGroup) =>
    getProductGroupLockedValue(name, selectedGroup);

  const parentPrices = useStore(form.store, (s) => ({
    unitPriceAmount: s.values.unitPriceAmount,
    takeawayPriceAmount: s.values.takeawayPriceAmount,
    purchasePriceAmount: s.values.purchasePriceAmount,
    barcodeCode: s.values.barcodeCode,
    barcodeType: s.values.barcodeType,
    articleNumber: s.values.articleNumber,
  }));
  const toMoney = (amountMajor: number) => ({
    amount: Math.round(Number(amountMajor) * 100),
    currency: storeCurrency,
  });
  const toOptionalMoney = (amountMajor: number) => {
    const money = toMoney(amountMajor);
    return money.amount > 0 ? money : null;
  };
  const variantExtraFields = showExtra ? (extraRows ?? []) : [];

  const hasVariants = variantsLen > 0;
  const showStockField = simpleStock && !hasVariants && !stockByLocation;
  const showLocationStock = stockByLocation && !hasVariants;

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <ProductImportSection title={t("productForm.sections.basicInformationTitle")}>
        <FormGrid>
          <GridFull>
            {chainField("name", <form.Field name="name">
              {(field) => (
                <FormGroup>
                  <FormLabel>{t("productForm.name")}</FormLabel>
                  <Input
                    type="text"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    required
                  />
                </FormGroup>
              )}
            </form.Field>)}
          </GridFull>
          {showGroups && (
            <GridFull>
              {chainField(null, <form.Field name="groupId">
                {(field) => (
                  <FormGroup>
                    <FormLabel>{t("productForm.productGroup")}</FormLabel>
                    <SelectInput
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    >
                      <option value="">{t("productForm.none")}</option>
                      {productGroups?.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </SelectInput>
                  </FormGroup>
                )}
              </form.Field>)}
            </GridFull>
          )}
          <GridCell>
            {chainField("unitPrice", <form.Field name="unitPriceAmount">
              {(field) => (
                <MoneyInput
                  label={t("productForm.unitPrice")}
                  amount={field.state.value}
                  currency={storeCurrency}
                  onAmountChange={field.handleChange}
                  onAmountBlur={field.handleBlur}
                  required
                />
              )}
            </form.Field>)}
          </GridCell>
          <GridCell>
            {chainField(null, <form.Field name="VAT">
              {(field) => {
                const isL = locked("VAT");
                const display = isL
                  ? String(lockedVal("VAT") ?? "0")
                  : String(field.state.value);
                return (
                  <FormGroup>
                    <FormLabel>{t("productForm.vat")}</FormLabel>
                    <SelectInput
                      value={display}
                      disabled={isL}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    >
                      {VAT_OPTIONS.map((v) => (
                        <option key={v} value={v}>
                          {v}%
                        </option>
                      ))}
                    </SelectInput>
                  </FormGroup>
                );
              }}
            </form.Field>)}
          </GridCell>
          <GridFull>
            {chainField(null, <form.Field name="unitId">
              {(field) => {
                const isL = locked("unitId");
                const lv = lockedVal("unitId") as string | null | undefined;
                const val = isL ? (lv ?? "") : field.state.value;
                return (
                  <FormGroup>
                    <FormLabel>{t("productForm.unit")}</FormLabel>
                    <SelectInput
                      value={val}
                      disabled={isL}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    >
                      <option value="">{t("productForm.none")}</option>
                      {units?.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </SelectInput>
                  </FormGroup>
                );
              }}
            </form.Field>)}
          </GridFull>
          <GridCell>
            {chainField(null, <form.Field name="openPrice">
              {(field) => (
                <FormGroup>
                  <ToggleRow>
                    <FormLabel>{t("productForm.openPrice")}</FormLabel>
                    <Toggle
                      checked={field.state.value}
                      onChange={field.handleChange}
                      onBlur={field.handleBlur}
                    />
                  </ToggleRow>
                </FormGroup>
              )}
            </form.Field>)}
          </GridCell>
          {showSerialTracking && (
            <GridCell>
              <form.Field name="serialTracking">
                {(field) => (
                  <FormGroup>
                    <ToggleRow>
                      <FormLabel>{t("productForm.serialTracking")}</FormLabel>
                      <Toggle
                        checked={field.state.value}
                        onChange={field.handleChange}
                        onBlur={field.handleBlur}
                      />
                    </ToggleRow>
                  </FormGroup>
                )}
              </form.Field>
            </GridCell>
          )}
        </FormGrid>
      </ProductImportSection>

      {showPrep && (
        <ProductImportSection title={t("productForm.sections.restaurantTitle")}>
          <FormGrid>
            <GridFull>
              {chainField(null, <form.Field name="courseId">
                {(field) => {
                  const isL = locked("courseId");
                  const lv = lockedVal("courseId") as string | null | undefined;
                  const val = isL ? (lv ?? "") : field.state.value;
                  return (
                    <FormGroup>
                      <FormLabel>{t("productForm.course")}</FormLabel>
                      <SelectInput
                        value={val}
                        disabled={isL}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      >
                        <option value="">{t("productForm.none")}</option>
                        {courses?.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </SelectInput>
                    </FormGroup>
                  );
                }}
              </form.Field>)}
            </GridFull>
            <GridFull>
              {chainField("prepStationId", <form.Field name="prepStationId">
                {(field) => (
                  <FormGroup>
                    <FormLabel>{t("productForm.prepStation")}</FormLabel>
                    <SelectInput
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    >
                      <option value="">{t("productForm.none")}</option>
                      {prepStations?.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </SelectInput>
                  </FormGroup>
                )}
              </form.Field>)}
            </GridFull>
          </FormGrid>
        </ProductImportSection>
      )}

      {showInfoBox && (
        <ProductImportSection title={t("productForm.sections.informationTitle")}>
          {chainField(null, <form.Field name="information">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("productForm.information")}</FormLabel>
                <TextArea
                  rows={4}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>)}
        </ProductImportSection>
      )}

      {showRetail && (
        <ProductImportSection title={t("productForm.sections.retailTitle")}>
          {barcodeActive && (
            <FormGrid>
              <GridCell>
                {chainField(null, <form.Field name="barcodeCode">
                  {(field) => (
                    <FormGroup>
                      <FormLabel>{t("productForm.barcodeCode")}</FormLabel>
                      <Input
                        type="text"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </FormGroup>
                  )}
                </form.Field>)}
              </GridCell>
              <GridCell>
                {chainField(null, <form.Field name="barcodeType">
                  {(field) => (
                    <FormGroup>
                      <FormLabel>{t("productForm.barcodeType")}</FormLabel>
                      <SelectInput
                        value={field.state.value}
                        onChange={(e) =>
                          field.handleChange(e.target.value as BarcodeType)
                        }
                        onBlur={field.handleBlur}
                      >
                        {Object.values(BarcodeType).map((bt) => (
                          <option key={bt} value={bt}>
                            {bt}
                          </option>
                        ))}
                      </SelectInput>
                    </FormGroup>
                  )}
                </form.Field>)}
              </GridCell>
              <GridFull>
                {chainField(null, <form.Field name="barcodeFormatId">
                  {(field) => (
                    <FormGroup>
                      <FormLabel>{t("productForm.barcodeFormatId")}</FormLabel>
                      <Input
                        type="text"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </FormGroup>
                  )}
                </form.Field>)}
              </GridFull>
            </FormGrid>
          )}
          {showLocationStock && (
            <FormGrid>
              {stock.locations.map((location) => (
                <GridCell key={location.id}>
                  <FormGroup>
                    <FormLabel>
                      {t("productForm.stockCount")} – {location.name}
                      {location.label ? ` (${location.label})` : ""}
                    </FormLabel>
                    <Input
                      type="number"
                      value={stock.getQuantity(location.id, null) ?? ""}
                      disabled={stock.isLoading}
                      onChange={(e) =>
                        stock.setQuantity(
                          location.id,
                          [null],
                          e.target.value.trim() === ""
                            ? null
                            : Number(e.target.value),
                        )
                      }
                    />
                  </FormGroup>
                </GridCell>
              ))}
            </FormGrid>
          )}
          {showStockField && (
            <FormGrid>
              <GridFull>
                {chainField(null, <form.Field name="stockCount">
                  {(field) => (
                    <FormGroup>
                      <FormLabel>{t("productForm.stockCount")}</FormLabel>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </FormGroup>
                  )}
                </form.Field>)}
              </GridFull>
            </FormGrid>
          )}
          <FormGrid>
            <GridFull>
              {chainField(null, <form.Field name="articleNumber">
                {(field) => (
                  <FormGroup>
                    <FormLabel>{t("productForm.articleNumber")}</FormLabel>
                    <Input
                      type="text"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormGroup>
                )}
              </form.Field>)}
            </GridFull>
            <GridCell>
              {chainField(null, <form.Field name="manufacturerArticleNumber">
                {(field) => (
                  <FormGroup>
                    <FormLabel>
                      {t("productForm.manufacturerArticleNumber")}
                    </FormLabel>
                    <Input
                      type="text"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormGroup>
                )}
              </form.Field>)}
            </GridCell>
            <GridCell>
              {chainField(null, <form.Field name="manufacturerBarcode">
                {(field) => (
                  <FormGroup>
                    <FormLabel>{t("productForm.manufacturerBarcode")}</FormLabel>
                    <Input
                      type="text"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormGroup>
                )}
              </form.Field>)}
            </GridCell>
            {showPurchasePrice && (
              <GridFull>
                {chainField(null, <form.Field name="purchasePriceAmount">
                  {(field) => (
                    <MoneyInput
                      label={t("productForm.purchasePrice")}
                      amount={field.state.value}
                      currency={storeCurrency}
                      onAmountChange={field.handleChange}
                      onAmountBlur={field.handleBlur}
                    />
                  )}
                </form.Field>)}
              </GridFull>
            )}
          </FormGrid>
        </ProductImportSection>
      )}

      {showExtra && extraRows && (
        <ProductImportSection title={t("productForm.sections.extraTitle")}>
          {chainField(null, <form.Field name="extraFields">
            {(field) => (
              <FormGrid>
                {extraRows.map((row) => (
                  <GridCell key={row.key}>
                    <FormGroup>
                      <FormLabel>{row.name}</FormLabel>
                      <Input
                        type="text"
                        value={field.state.value[row.key] ?? ""}
                        onChange={(e) =>
                          field.handleChange({
                            ...field.state.value,
                            [row.key]: e.target.value,
                          })
                        }
                        onBlur={field.handleBlur}
                      />
                    </FormGroup>
                  </GridCell>
                ))}
              </FormGrid>
            )}
          </form.Field>)}
        </ProductImportSection>
      )}

      {showVariants && (
        <ProductImportSection title={t("productForm.sections.variantsTitle")}>
          <form.Field name="variants">
            {(field) => (
              <>
                <ProductVariantsTable
                  variants={field.state.value}
                  onVariantsChange={field.handleChange}
                  parent={{
                    unitPrice: toMoney(parentPrices.unitPriceAmount),
                    takeawayPrice: toOptionalMoney(
                      parentPrices.takeawayPriceAmount,
                    ),
                    purchasePrice: toOptionalMoney(
                      parentPrices.purchasePriceAmount,
                    ),
                    barcodeCode: parentPrices.barcodeCode,
                    barcodeType: parentPrices.barcodeType,
                    articleNumber: parentPrices.articleNumber,
                  }}
                  currency={storeCurrency}
                  extraFields={variantExtraFields}
                  showBarcode={barcodeActive}
                  showTakeawayPrice={showTakeaway}
                  showPurchasePrice={showPurchasePrice}
                  showStock={simpleStock}
                  showPolicies={showStockPolicies}
                  stockPolicies={stockPolicies}
                  stock={stock}
                  onManage={() => setIsManagingVariants(true)}
                  chain={variantsChain}
                />
                {isManagingVariants && (
                  <VariantOptionsModal
                    variants={field.state.value}
                    extraFields={variantExtraFields}
                    storeId={storeId ?? null}
                    barcodeType={
                      store?.barcodeGenerationType ===
                      LabelDrawableBarcodeSymbology.EAN8
                        ? BarcodeType.EAN_8
                        : BarcodeType.EAN_13
                    }
                    canGenerateBarcodes={barcodeActive && !isChain && !!storeId}
                    hasStock={(variant) =>
                      stockByLocation
                        ? stock.hasStock(variant.id)
                        : (variant.stockCount ?? 0) > 0
                    }
                    onSave={(variants) => {
                      field.handleChange(variants);
                      setIsManagingVariants(false);
                    }}
                    onClose={() => setIsManagingVariants(false)}
                  />
                )}
              </>
            )}
          </form.Field>
        </ProductImportSection>
      )}

      {showModifiers && (
        <ProductImportSection title={t("productForm.sections.modifiersTitle")}>
          {chainField(null, <form.Field name="modifierGroups">
            {(field) => (
              <CheckboxList>
                {modifierGroupsList?.map((mg) => (
                  <CheckboxLabel key={mg.id}>
                    <input
                      type="checkbox"
                      checked={field.state.value.includes(mg.id)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...field.state.value, mg.id]
                          : field.state.value.filter((id) => id !== mg.id);
                        field.handleChange(next);
                      }}
                    />
                    {mg.name}
                  </CheckboxLabel>
                ))}
              </CheckboxList>
            )}
          </form.Field>)}
          {chainField(null, <form.Field name="modifiers">
            {(field) => {
              const isL = locked("modifiers");
              const display = isL
                ? ((lockedVal("modifiers") as ModifierId[]) ?? [])
                : field.state.value;
              return (
                <CheckboxList>
                  {modifiersList?.map((m) => (
                    <CheckboxLabel key={m.id}>
                      <input
                        type="checkbox"
                        checked={display.includes(m.id)}
                        disabled={isL}
                        onChange={(e) => {
                          if (isL) return;
                          const next = e.target.checked
                            ? [...field.state.value, m.id]
                            : field.state.value.filter((id) => id !== m.id);
                          field.handleChange(next);
                        }}
                      />
                      {m.name}
                    </CheckboxLabel>
                  ))}
                </CheckboxList>
              );
            }}
          </form.Field>)}
          {chainField(null, <form.Field name="alwaysAppliedModifiers">
            {(field) => {
              const isL = locked("alwaysAppliedModifiers");
              const display = isL
                ? ((lockedVal("alwaysAppliedModifiers") as ModifierId[]) ??
                  [])
                : field.state.value;
              return (
                <CheckboxList>
                  {modifiersList?.map((m) => (
                    <CheckboxLabel key={m.id}>
                      <input
                        type="checkbox"
                        checked={display.includes(m.id)}
                        disabled={isL}
                        onChange={(e) => {
                          if (isL) return;
                          const next = e.target.checked
                            ? [...field.state.value, m.id]
                            : field.state.value.filter((id) => id !== m.id);
                          field.handleChange(next);
                        }}
                      />
                      {m.name}
                    </CheckboxLabel>
                  ))}
                </CheckboxList>
              );
            }}
          </form.Field>)}
        </ProductImportSection>
      )}

      <ProductImportSection title={t("productForm.sections.bookkeepingTitle")}>
        <FormGrid>
          <GridCell>
            {chainField(null, <form.Field name="bookkeepingType">
              {(field) => {
                const isL = locked("type");
                const display = isL
                  ? ((lockedVal("type") as ProductType) ?? ProductType.PRODUCT)
                  : field.state.value;
                return (
                  <FormGroup>
                    <FormLabel>{t("productForm.productType")}</FormLabel>
                    <SelectInput
                      value={display}
                      disabled={isL}
                      onChange={(e) =>
                        field.handleChange(e.target.value as ProductType)
                      }
                      onBlur={field.handleBlur}
                    >
                      <option value={ProductType.PRODUCT}>
                        {t("productForm.typePRODUCT")}
                      </option>
                      <option value={ProductType.SERVICE}>
                        {t("productForm.typeSERVICE")}
                      </option>
                    </SelectInput>
                  </FormGroup>
                );
              }}
            </form.Field>)}
          </GridCell>
          <GridCell>
            {chainField("bookkeepingAccountId", <form.Field name="bookkeepingAccountId">
              {(field) => {
                const isL = locked("bookkeepingAccountId");
                const lv = lockedVal("bookkeepingAccountId") as
                  | string
                  | null
                  | undefined;
                const val = isL ? (lv ?? "") : field.state.value;
                return (
                  <FormGroup>
                    <FormLabel>{t("productForm.bookkeepingAccountId")}</FormLabel>
                    <SelectInput
                      value={val}
                      disabled={isL}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    >
                      <option value="">{t("productForm.none")}</option>
                      {bookkeepingAccounts?.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.accountNumber} {a.name}
                        </option>
                      ))}
                    </SelectInput>
                  </FormGroup>
                );
              }}
            </form.Field>)}
          </GridCell>
        </FormGrid>
      </ProductImportSection>

      {showTakeaway && (
        <ProductImportSection title={t("productForm.sections.takeawayTitle")}>
          <FormGrid>
            <GridCell>
              {chainField(null, <form.Field name="takeawayVAT">
                {(field) => {
                  const isL = locked("takeawayVAT");
                  const display = isL
                    ? String(lockedVal("takeawayVAT") ?? "")
                    : field.state.value;
                  return (
                    <FormGroup>
                      <FormLabel>{t("productForm.takeawayVat")}</FormLabel>
                      <SelectInput
                        value={display === "" ? "" : display}
                        disabled={isL}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      >
                        <option value="">{t("productForm.notSet")}</option>
                        {VAT_OPTIONS.map((v) => (
                          <option key={v} value={String(v)}>
                            {v}%
                          </option>
                        ))}
                      </SelectInput>
                    </FormGroup>
                  );
                }}
              </form.Field>)}
            </GridCell>
            <GridCell>
              {chainField(null, <form.Field name="takeawayBookkeepingAccountId">
                {(field) => {
                  const isL = locked("takeawayBookkeepingAccountId");
                  const lv = lockedVal(
                    "takeawayBookkeepingAccountId",
                  ) as string | null | undefined;
                  const val = isL ? (lv ?? "") : field.state.value;
                  return (
                    <FormGroup>
                      <FormLabel>
                        {t("productForm.takeawayBookkeepingAccount")}
                      </FormLabel>
                      <SelectInput
                        value={val}
                        disabled={isL}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      >
                        <option value="">{t("productForm.none")}</option>
                        {bookkeepingAccounts?.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.accountNumber} {a.name}
                          </option>
                        ))}
                      </SelectInput>
                    </FormGroup>
                  );
                }}
              </form.Field>)}
            </GridCell>
            <GridFull>
              {chainField("takeawayPrice", <form.Field name="takeawayPriceAmount">
                {(field) => (
                  <MoneyInput
                    label={t("productForm.takeawayPrice")}
                    amount={field.state.value}
                    currency={storeCurrency}
                    onAmountChange={field.handleChange}
                    onAmountBlur={field.handleBlur}
                  />
                )}
              </form.Field>)}
            </GridFull>
          </FormGrid>
        </ProductImportSection>
      )}

      {product && onDelete && (
        <ProductImportSection title={t("productForm.sections.dangerTitle")}>
          <Button
            type="button"
            onClick={onDelete}
            disabled={isLoading}
          >
            {t("productForm.deleteProduct")}
          </Button>
        </ProductImportSection>
      )}

      <FormFooter>
        <FormActions>
          <Button type="button" $color="secondary" onClick={onCancel} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? t("common.saving")
              : product
                ? t("common.update")
                : t("common.create")}
          </Button>
        </FormActions>
      </FormFooter>
    </Form>
  );
};
